import React, { useState, useEffect } from 'react'
import { Outlet, Navigate, useLocation } from 'react-router-dom'
import UserAuthContainer from './components/Containers/UserAuthContainer'
import UIStateContainer from './components/Containers/UIStateContainer'
import AuthTokenExpiredError from './components/Api/AuthTokenExpiredError'
import GuestReestablishRequiredError from './components/Api/GuestReestablishRequiredError'
import ROUTES from './routesConfig'

const ProtectedLayout = () => {
  const auth   = UserAuthContainer.useContainer()
  const ui     = UIStateContainer.useContainer()
  const location = useLocation()

  const [loading, setLoading] = useState(true)
  const [expiredType, setExpiredType] = useState(null)
  const [reestablishMeta, setReestablishMeta] = useState(null)

  useEffect(() => {
    const bootstrap = async () => {
      ui.startProgress()
      try {
        await auth.loadUser()
      } catch (err) {
        if (err instanceof GuestReestablishRequiredError) {
          setReestablishMeta(err.meta || null)
          setExpiredType('reestablish')
        } else if (err instanceof AuthTokenExpiredError) {
          //
          // Capture what kind of account we *had* when the token was
          // rejected so we can route the user to the right recovery
          // flow after we clear local auth.
          //
          let type = 'unknown'
          try {
            if (auth.hasRegisteredToken()) {
              type = 'registered'
            } else if (auth.hasAuthToken()) {
              // If we had some auth token but it's not registered, treat it
              // as a guest expiry.
              type = 'guest'
            }
          } catch (_) {
            // Fall through with type = 'unknown'
          }

          // clear out any stale guest/registered token
          auth.clearUserAndToken()
          setExpiredType(type)
        } else {
          // you could handle other errors here if you like
          console.error(err)
        }
      } finally {
        ui.progressComplete()
        setLoading(false)
      }
    }
    bootstrap()
    // `auth` and `ui` are container values (new object each render); we only
    // want to bootstrap once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // still bootstrapping?
  if (loading) return null

  //
  // Expired token – send the user back through the appropriate auth entry
  // point, preserving the original route (including any state) so that
  // after they recover access we can drop them back where they started.
  //
  if (expiredType === 'reestablish') {
    return (
      <Navigate
        to={ROUTES.GUEST_EXPIRED}
        state={{ from: location, reestablishMeta }}
        replace
      />
    )
  }

  if (expiredType === 'registered') {
    return (
      <Navigate
        to={ROUTES.LOGIN}
        state={{ from: location }}
        replace
      />
    )
  }

  if (expiredType === 'guest' || expiredType === 'unknown') {
    return (
      <Navigate
        to={ROUTES.GUEST_EXPIRED}
        state={{ from: location, notice: 'guest_expired' }}
        replace
      />
    )
  }

  // not authed at all → send through guest-signup, preserving original route
  if (!auth.isAuthed()) {
    return (
      <Navigate
        to={ROUTES.GUEST_SIGNUP}
        state={{ from: location }}
        replace
      />
    )
  }

  // finally, render the protected outlet
  return <Outlet />
}

export default ProtectedLayout
